/**
 * Double Ratchet (Signal, «The Double Ratchet Algorithm», rev. 1) поверх
 * примитивов Web Crypto. Состояние — обычный объект с байтами: оно живёт в
 * IndexedDB между запусками, поэтому приватный ключ рэтчета хранится сырыми
 * 32 байтами, а не `CryptoKey` (см. `primitives.ts` про извлекаемость).
 *
 * Все операции чистые: принимают состояние, возвращают НОВОЕ состояние.
 * При неудачной расшифровке старое состояние остаётся нетронутым — так
 * требует спецификация (§ «Decrypting messages»): испорченное сообщение не
 * должно сдвигать цепочку.
 *
 *   KDF_RK(rk, dh) = HKDF(salt=rk, ikm=dh, info=root)  → rk' ‖ ck
 *   KDF_CK(ck)     = HMAC(ck, 0x01) → mk;  HMAC(ck, 0x02) → ck'
 *   mk             → HKDF(mk, info=msg) → ключ AES-256 ‖ IV
 */
import { equal, toBase64, utf8 } from './bytes'
import {
  aesGcmDecrypt,
  aesGcmEncrypt,
  dh,
  exportDhPrivate,
  generateDh,
  hkdfSha256,
  hmacSha256,
  importDhPrivate,
  KEY_BYTES,
} from './primitives'

const INFO_ROOT = utf8('DvizhSecretChat/root/v1')
const INFO_MESSAGE = utf8('DvizhSecretChat/msg/v1')
const ZERO_SALT = new Uint8Array(KEY_BYTES)
const CHAIN_MESSAGE = new Uint8Array([0x01])
const CHAIN_NEXT = new Uint8Array([0x02])

/** Сколько сообщений одной цепочки можно пропустить (потерянные/переставленные). */
export const MAX_SKIP = 1000
/** Сколько ключей пропущенных сообщений хранится; старые вытесняются. */
export const MAX_SKIPPED_KEYS = 2000

export interface RatchetHeader {
  /** Публичный ключ рэтчета отправителя (DHs). */
  ratchetKey: Uint8Array
  /** Длина предыдущей цепочки отправки (PN). */
  previousCount: number
  /** Номер сообщения в текущей цепочке (N). */
  count: number
}

export interface SkippedKey {
  /** Base64 ключа рэтчета + номер — ключ поиска в списке. */
  ratchetKey: string
  count: number
  messageKey: Uint8Array
}

export interface RatchetState {
  rootKey: Uint8Array
  sendingPrivate: Uint8Array
  sendingPublic: Uint8Array
  remoteRatchetKey: Uint8Array | null
  sendingChain: Uint8Array | null
  receivingChain: Uint8Array | null
  sendCount: number
  receiveCount: number
  previousSendCount: number
  skipped: SkippedKey[]
}

async function kdfRoot(rootKey: Uint8Array, dhOut: Uint8Array): Promise<[Uint8Array, Uint8Array]> {
  const out = await hkdfSha256(dhOut, rootKey, INFO_ROOT, 64)
  return [out.slice(0, 32), out.slice(32, 64)]
}

async function kdfChain(chainKey: Uint8Array): Promise<[Uint8Array, Uint8Array]> {
  const messageKey = await hmacSha256(chainKey, CHAIN_MESSAGE)
  const nextChain = await hmacSha256(chainKey, CHAIN_NEXT)
  return [nextChain, messageKey]
}

async function messageKeyMaterial(messageKey: Uint8Array): Promise<{ key: Uint8Array; iv: Uint8Array }> {
  const out = await hkdfSha256(messageKey, ZERO_SALT, INFO_MESSAGE, 44)
  return { key: out.slice(0, 32), iv: out.slice(32, 44) }
}

function cloneState(state: RatchetState): RatchetState {
  return { ...state, skipped: [...state.skipped] }
}

/** Сторона-инициатор: у неё уже есть SK из X3DH и signed prekey собеседника. */
export async function initAsInitiator(
  sharedSecret: Uint8Array,
  remoteRatchetKey: Uint8Array,
): Promise<RatchetState> {
  const sending = await generateDh(true)
  const sendingPrivate = await exportDhPrivate(sending.privateKey)
  const [rootKey, sendingChain] = await kdfRoot(sharedSecret, await dh(sending.privateKey, remoteRatchetKey))
  return {
    rootKey,
    sendingPrivate,
    sendingPublic: sending.publicKey,
    remoteRatchetKey,
    sendingChain,
    receivingChain: null,
    sendCount: 0,
    receiveCount: 0,
    previousSendCount: 0,
    skipped: [],
  }
}

/** Отвечающая сторона: её первая пара рэтчета — собственный signed prekey. */
export function initAsResponder(
  sharedSecret: Uint8Array,
  signedPrekeyPrivate: Uint8Array,
  signedPrekeyPublic: Uint8Array,
): RatchetState {
  return {
    rootKey: sharedSecret,
    sendingPrivate: signedPrekeyPrivate,
    sendingPublic: signedPrekeyPublic,
    remoteRatchetKey: null,
    sendingChain: null,
    receivingChain: null,
    sendCount: 0,
    receiveCount: 0,
    previousSendCount: 0,
    skipped: [],
  }
}

export interface EncryptResult {
  state: RatchetState
  header: RatchetHeader
  ciphertext: Uint8Array
}

/**
 * `aad` — X3DH associated data плюс сериализованный заголовок конверта:
 * вызывающий код (`envelope.ts`) строит его сам, потому что формат заголовка
 * принадлежит конверту, а не рэтчету.
 */
export async function ratchetEncrypt(
  state: RatchetState,
  plaintext: Uint8Array,
  aad: (header: RatchetHeader) => Uint8Array,
): Promise<EncryptResult> {
  if (!state.sendingChain) throw new Error('secret_session_not_ready')
  const next = cloneState(state)
  const [chain, messageKey] = await kdfChain(state.sendingChain)
  next.sendingChain = chain
  const header: RatchetHeader = {
    ratchetKey: state.sendingPublic,
    previousCount: state.previousSendCount,
    count: state.sendCount,
  }
  next.sendCount = state.sendCount + 1
  const { key, iv } = await messageKeyMaterial(messageKey)
  const ciphertext = await aesGcmEncrypt(key, iv, plaintext, aad(header))
  return { state: next, header, ciphertext }
}

async function trySkipped(
  state: RatchetState,
  header: RatchetHeader,
  ciphertext: Uint8Array,
  aad: Uint8Array,
): Promise<{ state: RatchetState; plaintext: Uint8Array } | null> {
  const lookup = toBase64(header.ratchetKey)
  const index = state.skipped.findIndex((s) => s.ratchetKey === lookup && s.count === header.count)
  if (index < 0) return null
  const entry = state.skipped[index]
  if (!entry) return null
  const { key, iv } = await messageKeyMaterial(entry.messageKey)
  const plaintext = await aesGcmDecrypt(key, iv, ciphertext, aad)
  const next = cloneState(state)
  next.skipped.splice(index, 1)
  return { state: next, plaintext }
}

async function skipMessageKeys(state: RatchetState, until: number): Promise<void> {
  if (!state.receivingChain || !state.remoteRatchetKey) return
  if (state.receiveCount + MAX_SKIP < until) throw new Error('secret_too_many_skipped')
  const ratchetKey = toBase64(state.remoteRatchetKey)
  let chain = state.receivingChain
  while (state.receiveCount < until) {
    const [nextChain, messageKey] = await kdfChain(chain)
    chain = nextChain
    state.skipped.push({ ratchetKey, count: state.receiveCount, messageKey })
    state.receiveCount += 1
  }
  state.receivingChain = chain
  if (state.skipped.length > MAX_SKIPPED_KEYS)
    state.skipped.splice(0, state.skipped.length - MAX_SKIPPED_KEYS)
}

async function dhRatchet(state: RatchetState, header: RatchetHeader): Promise<void> {
  state.previousSendCount = state.sendCount
  state.sendCount = 0
  state.receiveCount = 0
  state.remoteRatchetKey = header.ratchetKey
  const current = await importDhPrivate(state.sendingPrivate)
  ;[state.rootKey, state.receivingChain] = await kdfRoot(state.rootKey, await dh(current, header.ratchetKey))
  const fresh = await generateDh(true)
  state.sendingPrivate = await exportDhPrivate(fresh.privateKey)
  state.sendingPublic = fresh.publicKey
  ;[state.rootKey, state.sendingChain] = await kdfRoot(
    state.rootKey,
    await dh(fresh.privateKey, header.ratchetKey),
  )
}

export interface DecryptResult {
  state: RatchetState
  plaintext: Uint8Array
}

export async function ratchetDecrypt(
  state: RatchetState,
  header: RatchetHeader,
  ciphertext: Uint8Array,
  aad: Uint8Array,
): Promise<DecryptResult> {
  const fromSkipped = await trySkipped(state, header, ciphertext, aad)
  if (fromSkipped) return fromSkipped

  const next = cloneState(state)
  if (!next.remoteRatchetKey || !equal(next.remoteRatchetKey, header.ratchetKey)) {
    await skipMessageKeys(next, header.previousCount)
    await dhRatchet(next, header)
  }
  await skipMessageKeys(next, header.count)
  if (!next.receivingChain) throw new Error('secret_session_not_ready')
  const [chain, messageKey] = await kdfChain(next.receivingChain)
  next.receivingChain = chain
  next.receiveCount += 1
  const { key, iv } = await messageKeyMaterial(messageKey)
  // Расшифровка — последней: если тег не сошёлся, `next` просто выбрасывается.
  const plaintext = await aesGcmDecrypt(key, iv, ciphertext, aad)
  return { state: next, plaintext }
}
